import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, onChange, textarea, placeholder }) => (
  <div>
    <label className="mb-1.5 block text-xs uppercase tracking-widest text-smoke">{label}</label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="w-full resize-none rounded-lg border border-[#2a2a2a] bg-void px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-gold"
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-gold"
      />
    )}
  </div>
);

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-line bg-surface p-6">
    <h3 className="font-display text-xl text-gold mb-5">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export const ContentEditor = () => {
  const { content, setContent, fetchContent } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  if (!draft) return <div className="text-smoke animate-pulse">Loading...</div>;

  const update = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const updateArrayItem = (section, index, key, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[section][index][key] = value;
      return next;
    });
  };

  const addArrayItem = (section, template) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[section] = next[section] || [];
      next[section].push({ ...template, id: `${section[0]}${Date.now()}` });
      return next;
    });
  };

  const removeArrayItem = (section, index) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[section].splice(index, 1);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", { content: draft });
      const clean = { ...data };
      delete clean.updated_at;
      setContent(draft);
      await fetchContent();
      toast.success("Content saved!", { description: "Your website has been updated." });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-editor">
      <div className="mb-8 flex items-center justify-between sticky top-0 z-10 bg-[#0a0a0a]/80 backdrop-blur-md py-4 -mx-2 px-2">
        <div>
          <h2 className="font-display text-3xl text-ink">Content Editor</h2>
          <p className="text-sm text-smoke mt-1">Edit your website text and images.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content-btn"
          className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold uppercase tracking-widest text-black transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="space-y-6">
        {/* HERO */}
        <Card title="Hero Section">
          <Field label="Headline Line 1" value={draft.hero?.line1} onChange={(v) => update(["hero", "line1"], v)} />
          <Field label="Headline Line 2" value={draft.hero?.line2} onChange={(v) => update(["hero", "line2"], v)} />
          <Field label="Subtitle" value={draft.hero?.subtitle} textarea onChange={(v) => update(["hero", "subtitle"], v)} />
          <Field label="Accent Text" value={draft.hero?.accent} onChange={(v) => update(["hero", "accent"], v)} />
          <Field label="Background Image URL" value={draft.hero?.background_image} onChange={(v) => update(["hero", "background_image"], v)} />
        </Card>

        {/* MARQUEE */}
        <Card title="Marquee Band">
          <Field label="Marquee Text (separate words with — )" value={draft.marquee?.text} textarea onChange={(v) => update(["marquee", "text"], v)} />
        </Card>

        {/* ABOUT */}
        <Card title="About Section">
          <Field label="Heading" value={draft.about?.heading} onChange={(v) => update(["about", "heading"], v)} />
          <Field label="Intro" value={draft.about?.intro} textarea onChange={(v) => update(["about", "intro"], v)} />

          {/* Founder profile */}
          <div className="rounded-lg border border-[#2a2a2a] p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-smoke">Founder / About Me</p>
            <Field label="Name" value={draft.about?.profile?.name} onChange={(v) => update(["about", "profile", "name"], v)} />
            <Field label="Role" value={draft.about?.profile?.role} onChange={(v) => update(["about", "profile", "role"], v)} />
            <Field label="Bio / Description" value={draft.about?.profile?.bio} textarea onChange={(v) => update(["about", "profile", "bio"], v)} />
            <p className="text-xs uppercase tracking-widest text-smoke pt-1">Photos (4)</p>
            {[0, 1, 2, 3].map((idx) => (
              <Field
                key={idx}
                label={`Photo ${idx + 1} URL`}
                value={(draft.about?.profile?.photos || [])[idx]}
                onChange={(v) => {
                  setDraft((prev) => {
                    const n = JSON.parse(JSON.stringify(prev));
                    n.about.profile = n.about.profile || {};
                    n.about.profile.photos = n.about.profile.photos || [];
                    n.about.profile.photos[idx] = v;
                    return n;
                  });
                }}
              />
            ))}
          </div>
          <div className="pt-2">
            <p className="text-xs uppercase tracking-widest text-smoke mb-3">Chapters</p>
            <div className="space-y-4">
              {(draft.about?.chapters || []).map((ch, i) => (
                <div key={i} className="rounded-lg border border-[#2a2a2a] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gold">Chapter {ch.number}</span>
                    <button
                      onClick={() => {
                        setDraft((prev) => {
                          const next = JSON.parse(JSON.stringify(prev));
                          next.about.chapters.splice(i, 1);
                          return next;
                        });
                      }}
                      className="text-smoke hover:text-coral"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <Field label="Number" value={ch.number} onChange={(v) => {
                    setDraft((prev) => { const n = JSON.parse(JSON.stringify(prev)); n.about.chapters[i].number = v; return n; });
                  }} />
                  <Field label="Title" value={ch.title} onChange={(v) => {
                    setDraft((prev) => { const n = JSON.parse(JSON.stringify(prev)); n.about.chapters[i].title = v; return n; });
                  }} />
                  <Field label="Text" value={ch.text} textarea onChange={(v) => {
                    setDraft((prev) => { const n = JSON.parse(JSON.stringify(prev)); n.about.chapters[i].text = v; return n; });
                  }} />
                </div>
              ))}
              <button
                onClick={() => {
                  setDraft((prev) => {
                    const n = JSON.parse(JSON.stringify(prev));
                    n.about.chapters = n.about.chapters || [];
                    const num = String(n.about.chapters.length + 1).padStart(2, "0");
                    n.about.chapters.push({ number: num, title: "New Chapter", text: "" });
                    return n;
                  });
                }}
                className="flex items-center gap-2 text-sm text-gold hover:underline"
              >
                <Plus size={15} /> Add Chapter
              </button>
            </div>
          </div>
        </Card>

        {/* SERVICES */}
        <Card title="Services">
          <div className="space-y-4">
            {(draft.services || []).map((s, i) => (
              <div key={s.id || i} className="rounded-lg border border-[#2a2a2a] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gold">Service {i + 1}</span>
                  <button onClick={() => removeArrayItem("services", i)} className="text-smoke hover:text-coral">
                    <Trash2 size={16} />
                  </button>
                </div>
                <Field label="Title" value={s.title} onChange={(v) => updateArrayItem("services", i, "title", v)} />
                <Field label="Description" value={s.description} textarea onChange={(v) => updateArrayItem("services", i, "description", v)} />
                <Field label="Image URL" value={s.image} onChange={(v) => updateArrayItem("services", i, "image", v)} />
              </div>
            ))}
            <button
              onClick={() => addArrayItem("services", { title: "New Service", description: "", image: "" })}
              className="flex items-center gap-2 text-sm text-gold hover:underline"
            >
              <Plus size={15} /> Add Service
            </button>
          </div>
        </Card>

        {/* GALLERY */}
        <Card title="Gallery">
          <div className="grid sm:grid-cols-2 gap-4">
            {(draft.gallery || []).map((g, i) => (
              <div key={g.id || i} className="rounded-lg border border-[#2a2a2a] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gold">Image {i + 1}</span>
                  <button onClick={() => removeArrayItem("gallery", i)} className="text-smoke hover:text-coral">
                    <Trash2 size={16} />
                  </button>
                </div>
                <Field label="Image URL" value={g.image} onChange={(v) => updateArrayItem("gallery", i, "image", v)} />
                <Field label="Caption" value={g.caption} onChange={(v) => updateArrayItem("gallery", i, "caption", v)} />
              </div>
            ))}
          </div>
          <button
            onClick={() => addArrayItem("gallery", { image: "", caption: "New Image" })}
            className="mt-4 flex items-center gap-2 text-sm text-gold hover:underline"
          >
            <Plus size={15} /> Add Image
          </button>
        </Card>

        {/* TESTIMONIALS */}
        <Card title="Testimonials">
          <div className="space-y-4">
            {(draft.testimonials || []).map((t, i) => (
              <div key={t.id || i} className="rounded-lg border border-[#2a2a2a] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gold">Testimonial {i + 1}</span>
                  <button onClick={() => removeArrayItem("testimonials", i)} className="text-smoke hover:text-coral">
                    <Trash2 size={16} />
                  </button>
                </div>
                <Field label="Quote" value={t.quote} textarea onChange={(v) => updateArrayItem("testimonials", i, "quote", v)} />
                <Field label="Author" value={t.author} onChange={(v) => updateArrayItem("testimonials", i, "author", v)} />
                <Field label="Role" value={t.role} onChange={(v) => updateArrayItem("testimonials", i, "role", v)} />
              </div>
            ))}
            <button
              onClick={() => addArrayItem("testimonials", { quote: "", author: "", role: "" })}
              className="flex items-center gap-2 text-sm text-gold hover:underline"
            >
              <Plus size={15} /> Add Testimonial
            </button>
          </div>
        </Card>

        {/* CONTACT */}
        <Card title="Contact Information">
          <Field label="Email" value={draft.contact?.email} onChange={(v) => update(["contact", "email"], v)} />
          <Field label="Phone" value={draft.contact?.phone} onChange={(v) => update(["contact", "phone"], v)} />
          <Field label="Instagram URL" value={draft.contact?.instagram} onChange={(v) => update(["contact", "instagram"], v)} />
          <Field label="Facebook URL" value={draft.contact?.facebook} onChange={(v) => update(["contact", "facebook"], v)} />
        </Card>
      </div>
    </div>
  );
};
